import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconClose, IconLocate, IconPinSolid } from '../components/Icon';
import { useScheduleModel } from '../lib/schedule';
import { fmtDay, fmtTime } from '../lib/time';
import ResortPlan from '../components/ResortPlan';

/**
 * Map — NOT on the canvas (the "try next" line asks for it to be drawn), so
 * this is the schematic version: the resort plan, thumb-sized pins, and the
 * venue sheet the requirements describe. What it deliberately does not do is
 * pretend to be the real thing — there is no tile layer here, and the plan is a
 * drawing rather than a survey.
 *
 * Two behaviours are real and should survive whatever it grows into:
 * location permission is requested on tap and never on load, and an indoor
 * venue says its building and floor instead of relying on a GPS fix it will
 * not get.
 */
export default function MapPage() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const model = useScheduleModel();
  const [locating, setLocating] = useState<'idle' | 'asking' | 'granted' | 'denied'>('idle');
  const [me, setMe] = useState<{ x: number; y: number } | null>(null);

  const venues = model.events.length ? [...new Set(model.events.map((e) => e.venueId))].map((id) => model.venueOf(id)) : [];
  const focused = venueId ? model.venueOf(venueId) : undefined;
  const nextVenueId = model.next?.venueId ?? model.current?.venueId;

  function locate() {
    setLocating('asking');
    navigator.geolocation.getCurrentPosition(
      () => {
        // Without a survey of the resort there is no honest way to place a real
        // fix on this plan, so the dot lands on the lobby and says so.
        setMe({ x: 44, y: 52 });
        setLocating('granted');
      },
      () => setLocating('denied'),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  return (
    <div className="page page-fill">
      <div className="map-wrap">
        <ResortPlan />
        {venues.map((venue) => venue && (
          <button
            key={venue.id}
            type="button"
            className={`map-pin${venue.id === nextVenueId ? ' is-next' : ''}`}
            style={{ left: `${venue.x}%`, top: `${venue.y}%` }}
            onClick={() => navigate(`/map/${venue.id}`)}
          >
            <span className="dot"><span style={{ transform: 'rotate(45deg)', display: 'grid' }}><IconPinSolid size={14} /></span></span>
            <span className="label">{venue.name}</span>
          </button>
        ))}
        {me && (
          <span
            style={{
              position: 'absolute', left: `${me.x}%`, top: `${me.y}%`, transform: 'translate(-50%,-50%)',
              width: 18, height: 18, borderRadius: '50%', background: 'var(--color-accent-2-600)',
              boxShadow: '0 0 0 10px color-mix(in srgb, var(--color-accent-2-600) 22%, transparent)',
            }}
            aria-label="Roughly where you are"
          />
        )}

        <button
          type="button"
          className="btn btn-secondary"
          style={{ position: 'absolute', left: 16, bottom: 16, background: 'var(--color-bg)', gap: 8, minHeight: 44 }}
          onClick={locate}
          disabled={locating === 'asking'}
        >
          <IconLocate size={16} />
          {locating === 'asking' ? 'Looking…' : locating === 'denied' ? 'Location is off — the map still works' : 'Locate me'}
        </button>
      </div>

      {focused && (
        <div className="sheet">
          <div className="sheet-grab" />
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <span className="venue-thumb" aria-hidden="true">
              <ResortPlan className="venue-thumb-plan" focus={{ x: focused.x, y: focused.y }} label="" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4 style={{ margin: 0 }}>{focused.name}</h4>
              <div style={{ fontSize: 12.5, opacity: 0.7 }}>{focused.where}</div>
              {focused.indoor && (
                /* GPS does not work indoors and the design should not pretend
                   otherwise — say the floor and hand over to the signage. */
                <div style={{ fontSize: 12.5, opacity: 0.7, marginTop: 2 }}>
                  Indoors, so your phone will not find you in here. Follow the signs from reception.
                </div>
              )}
            </div>
            <button type="button" className="btn btn-icon btn-secondary" onClick={() => navigate('/map')} aria-label="Close">
              <IconClose size={16} />
            </button>
          </div>

          {(() => {
            const nextHere = model.events.find((e) => e.venueId === focused.id && model.stateOf(e) !== 'past');
            if (!nextHere) return <p style={{ fontSize: 12.5, opacity: 0.6, marginTop: 12 }}>Nothing else is happening here this week.</p>;
            return (
              <Link
                to={`/schedule/${nextHere.id}`}
                className="card"
                style={{ marginTop: 12, background: 'var(--color-surface)', color: 'inherit', textDecoration: 'none', padding: '12px 16px' }}
              >
                <div style={{ fontSize: 11, opacity: 0.6 }}>Next here</div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18 }}>{nextHere.name}</div>
                <div style={{ fontSize: 12.5, opacity: 0.7 }}>{fmtDay(nextHere.start)} · {fmtTime(nextHere.start)}</div>
              </Link>
            );
          })()}

          <a
            className="btn btn-primary btn-block" style={{ minHeight: 48 }}
            href={focused.navUrl} target="_blank" rel="noreferrer noopener"
          >
            Navigate
          </a>
        </div>
      )}
    </div>
  );
}
